/**
 * iDecide – Slider plugin
 * Add the panel options for sliders, and create HTML slider overlays from here.
 */

import CreativeEditorSDK, {type AssetResult} from '@cesdk/cesdk-js';
import type { CreativeEngine } from '@cesdk/cesdk-js';
import {BLOCK_TYPE, DYNAMIC_VARIABLES_UPDATED_EVENT, SLIDER_META_KEY} from "../constants.ts";
import {isEngineReady} from "../utils/utilities.ts";
import {positionOverlayToBlock, updateOverlayTimelineVisibility} from "../utils/interactionOverlay.ts";
import {findOwningPage} from "../utils/slideUtilities.ts";

/** --------------------------------------
 * Types & constants
 * ------------------------------------- */
export type SliderStep = { value: number; text: string };

export type SliderMeta = {
  id: string;
  elementType: string;
  min: number;
  max: number;
  step: number;
  value: number;
  orientation: 'horizontal' | 'vertical';
  label?: string;
  /** If true, only snap to `steps`; if false, use `min/max/step`. */
  discrete?: boolean;
  steps?: SliderStep[];
  style?: {
    heightPx?: number;
    trackHeightPx?: number;
    trackRadiusPx?: number;
    outlineColor?: string;
    outlineSizePx?: number;
    trackColor?: string;
    fillColor?: string;
    thumbColor?: string;
    thumbRadiusPx?: number;
    tickColor?: string;
    labelColor?: string;
    fontFamily?: string;
    fontSizePx?: number;
    labelUppercase?: boolean;
    showActiveLabelOverlay?: boolean;
  };
};

const SLIDER_SOURCE_ID = 'idecide.slider.source';
export const SLIDER_ENTRY_ID = 'idecide.sliders';

// Keep a single registry so overlays can be replaced in-place without flicker.
const SLIDER_OVERLAYS = new Map<number, HTMLDivElement>();

/** Inline SVG used as the Asset Library card preview. */
export const SLIDER_CARD_SVG = encodeURIComponent(`
<svg viewBox="0 0 258 214" xmlns="http://www.w3.org/2000/svg"><defs xmlns="http://www.w3.org/2000/svg"><linearGradient id="a" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#0989cf"/><stop offset="100%" stop-color="#1f61c1"/></linearGradient></defs><path d="M237.4 123.7h-215c-5.3 0-9.6-4.3-9.6-9.6s4.3-9.6 9.6-9.6h215c5.3 0 9.6 4.3 9.6 9.6.1 5.3-4.3 9.6-9.6 9.6z" fill="url(#a)"/><path d="M70.4 135.4c-11.8 0-21.3-9.6-21.3-21.3 0-11.8 9.6-21.3 21.3-21.3 11.8 0 21.3 9.6 21.3 21.3s-9.5 21.3-21.3 21.3z" fill="#d6dfed" stroke="url(#a)" stroke-miterlimit="10" stroke-width="18"/><g fill="url(#a)"><path d="M212.6 65.2H45.4c-.6 0-1-.5-1-1v-8c0-.6.5-1 1-1h167.2c.6 0 1 .5 1 1v8c.1.6-.4 1-1 1zm-15.9-25.5H61.4c-.6 0-1-.5-1-1v-8c0-.6.5-1 1-1h135.3c.6 0 1 .5 1 1v8c0 .6-.5 1-1 1z"/><circle cx="32.9" cy="174.4" r="10.1"/><circle cx="130.6" cy="174.4" r="10.1"/><circle cx="228.3" cy="174.4" r="10.1"/></g></svg>
`);

export function installSliderPlugin(
  cesdk: CreativeEditorSDK,
  options: { mountInteractiveOverlay?: boolean; autoOpenLibrary?: boolean } = {}
): { updateForActivePage: (pageId: number) => void; cleanup: () => void } {
  addSliderAssetSource(cesdk);

  // Asset Library entry
  cesdk.ui.addAssetLibraryEntry({
    id: SLIDER_ENTRY_ID,
    sourceIds: [SLIDER_SOURCE_ID],
    title: () => 'Sliders',
    previewLength:2,
    gridColumns: 2,
    gridItemHeight: 'square',
    cardBorder: false,
    cardLabel: () => 'Slider (3 steps)',
    cardBackgroundPreferences: [{ type: 'image' as any, path: 'meta.thumbUri' }],
    previewBackgroundType: 'cover',
  });

  // DOM overlays
  if (options.mountInteractiveOverlay !== false) {
    const engine = cesdk.engine;
    let overlayContainer = document.getElementById('overlay-container');
    if (!overlayContainer) {
      const host = document.getElementById("root-shadow");
      const root = host?.shadowRoot || (host as any)?.__shadowRoot;
      overlayContainer = root?.getElementById('overlay-container');
    }
    return mountSliderOverlays(engine, {
      container: overlayContainer,
      interactive: true,
      clearHTMLOverlays: true  // Clear any stale overlays from previous page
    });
  }

  // Return no-op controller if overlays are disabled
  return {
    updateForActivePage: () => {},
    cleanup: () => {}
  };
}

/** --------------------------------------
 * Asset Library – engine source
 * ------------------------------------- */
function addSliderAssetSource(cesdk: CreativeEditorSDK): void {
  const engine = cesdk.engine as CreativeEngine;

  const source: any = {
    id: SLIDER_SOURCE_ID,
    findAssets: async (queryData: any) => ({
      assets: [
        {
          id: 'idecide.slider.3',
          locale: 'en',
          label: 'Slider (3 steps)',
          tags: ['slider'],
          meta: {
            thumbUri: `data:image/svg+xml;utf8,${SLIDER_CARD_SVG}`,
            blockType: '//ly.img.ubq/graphic',
            width: 800,
            height: 64
          },
          context: { sourceId: SLIDER_SOURCE_ID }
        }
      ],
      total: 1,
      currentPage: queryData?.page ?? 0,
      nextPage: undefined
    }),

    /** Create and return a slider block. */
    applyAsset: async (_assetResult: AssetResult) => {
      const page = engine.scene.getCurrentPage?.();
      if (page) {
        const block = createSliderBlock(engine, page, undefined);

        // Select the block for immediate editing
        cesdk.engine.block.setSelected(block, true);
        // try { cesdk.ui.openPanel(SLIDER_PANEL_ID); } catch {}

        // return block;
      }
    }
  };

  try {
    const existing: any = (engine as any).asset?.findAllSources?.();
    if (!existing || (Array.isArray(existing) && !existing.includes(SLIDER_SOURCE_ID))) {
      engine.asset.addSource(source);
    }
  } catch (e) {
    console.error('Error adding slider asset source.', e);
  }
}

/** --------------------------------------
 * Overlays – DOM-based slider UI
 * ------------------------------------- */
export function mountSliderOverlays(
  engine: CreativeEngine,
  opts: { container?: HTMLElement | null;
          interactive?: boolean,
          clearHTMLOverlays?: boolean } = {}
): { updateForActivePage: (pageId: number) => void; cleanup: () => void } {
  const host = opts.container ?? (engine as any).element?.parentElement ?? document.body;
  let clearOverlays = opts.clearHTMLOverlays;

  const overlays = SLIDER_OVERLAYS;

  const rebuild = () => {
    const all: number[] = findAllSliders(engine);

    //Check for this flag to clear overlays the first time mountSliderOverlays is called on a new page
    if (clearOverlays){
      overlays.forEach((el) => {
        try { el.remove(); } catch {}
      });
      overlays.clear();
      clearOverlays = false;
    }else{
      // Remove stale overlays
      [...overlays.keys()].forEach((id) => {
        if (!all.includes(id)) {
          overlays.get(id)?.remove();
          overlays.delete(id);
        }
      });
    }
    // Add new overlays
    all.forEach((id) => {
      if (!overlays.has(id)) {
        const el = createSliderOverlay(engine, id, host, !!opts.interactive);
        overlays.set(id, el);
        requestAnimationFrame(() => positionOverlayToBlock(engine, id, el, updateSliderOverlayGeometry)); // initial layout
      }
    });
  };

  const layoutAll = () => overlays.forEach((el, id) => positionOverlayToBlock(engine, id, el, updateSliderOverlayGeometry));

  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      rebuild();
      layoutAll();
      // Update visibility after rebuild to handle slow timeline scrubbing
      // (onStateChanged doesn't fire continuously during slow drags)
      updateOverlayTimelineVisibility(engine, SLIDER_OVERLAYS, isEngineReady);
    });
  };

  // initial
  schedule();

  // Track current slider subscriptions
  let currentSliders: number[] = [];
  let unsubSliders: (() => void) | null = null;

  // Function to update slider subscriptions dynamically
  const updateSliderSubscriptions = () => {
    const sliders = findAllSliders(engine);

    // Only re-subscribe if the list of sliders changed
    if (JSON.stringify(sliders) !== JSON.stringify(currentSliders)) {
      // Unsubscribe from old sliders
      if (unsubSliders) {
        try {
          unsubSliders();
        } catch {}
      }

      // Subscribe to new sliders for position/metadata updates
      currentSliders = sliders;
      unsubSliders = sliders.length > 0
        ? engine.event?.subscribe?.(sliders, (events) => {
          try {
            // Filter out events for invalid/deleted blocks using same validation as hardenTimelineApi
            const validEvents = events.filter(e => {
              try {
                const id = e.block;
                // Check if ID is a valid positive integer before calling engine methods
                if (!Number.isInteger(id) || id <= 0 || id === 4294967295) {
                  return false;
                }
                return engine.block.isValid?.(id);
              } catch {
                return false;
              }
            });

            // Only rebuild/layout if sliders were actually updated
            const hasRelevantEvent = validEvents.some(e => e.type === 'Updated');
            if (hasRelevantEvent) {
              schedule();
            }
          } catch (error) {
            console.warn('Slider event handler error:', error);
          }
        })
        : null;
    }
  };

  // Track current page subscription and ID
  let unsubPages: (() => void) | null = null;
  let currentPageId = engine.scene?.getCurrentPage?.() ?? 0;

  // Update subscriptions when active page changes
  const updateForActivePage = (pageId: number) => {
    currentPageId = pageId;

    //Clear overlays from previous page

    // Remove stale overlays
    const all: number[] = findAllSliders(engine);
    [...overlays.keys()].forEach((id) => {
      if (!all.includes(id)) {
        overlays.get(id)?.remove();
        overlays.delete(id);
      }
    });

    // Clean up old page subscription
    if (unsubPages) {
      try { unsubPages(); } catch {}
    }

    // Subscribe to current page for Updated events to detect new sliders
    unsubPages = engine.event?.subscribe?.([pageId], (events) => {
      try {
        // Filter out events for invalid/deleted blocks using same validation as hardenTimelineApi
        const validEvents = events.filter(e => {
          try {
            const id = e.block;
            // Check if ID is a valid positive integer before calling engine methods
            if (!Number.isInteger(id) || id <= 0 || id === 4294967295) {
              return false;
            }
            return engine.block.isValid?.(id);
          } catch {
            return false;
          }
        });

        const hasUpdated = validEvents.some(e => e.type === 'Updated');
        if (hasUpdated) {
          schedule();
          // After rebuild, update subscriptions to include any new sliders
          requestAnimationFrame(() => {
            updateSliderSubscriptions();
          });
        }
      } catch (error) {
        // Ignore errors from invalid blocks
        console.warn('Slider page event handler error:', error);
      }
    });

    // Reposition overlays and update visibility for the new page
    requestAnimationFrame(() => {
      layoutAll();
      updateOverlayTimelineVisibility(engine, SLIDER_OVERLAYS, isEngineReady);
    });
  };

  // Initial subscription setup
  const initialPage = engine.scene?.getCurrentPage?.();
  if (initialPage) {
    updateForActivePage(initialPage);
  }
  updateSliderSubscriptions();

  // Listen to host resizes - reposition for current page
  const handleResize = () => {
    requestAnimationFrame(() => {
      layoutAll();
    });
  };

  const ro = new ResizeObserver(handleResize);
  ro.observe(host);

  // Listen to the CESDK wrapper for layout shifts (e.g. when panels open)
  const wrapper = host.closest('.cesdkWrapperStyle');
  if (wrapper) {
    ro.observe(wrapper);
  }

  // Listen to the custom panel change event
  window.addEventListener('cesdk-relayout', handleResize);

  // Listen to canvas zoom changes
  let zoomTimeout: number | null = null;
  const unsubZoomLevelChanged = engine.scene?.onZoomLevelChanged?.(() => {
    // Debounce zoom events to avoid excessive updates
    if (zoomTimeout) clearTimeout(zoomTimeout);
    zoomTimeout = window.setTimeout(() => {
      layoutAll();
    }, 50); // 50ms debounce - quick response for zoom
  });

  // Listen to playback time changes for visibility updates
  // Watch for playback time changes ONLY if there are sliders
  // This is much more efficient than onStateChanged which fires constantly
  let visibilityRafId: number | null = null;
  let unsubStateChanged: (() => void) | null = null;

    const sliders = findAllSliders(engine);
    if (sliders.length > 0) {
      // Try to find the playhead element in Shadow DOM (Builder mode)
      const rootEl = document.getElementById('root-shadow');
      const shadowRoot = rootEl?.shadowRoot;
      const playheadElement = shadowRoot?.querySelector('[role="slider"][aria-valuenow]');

      // Fallback: Try to find the review wrapper with data-playback-time (Review/Player mode)
      const reviewWrapper = document.querySelector('.review-cesdk-wrapper[data-playback-time]');

      const timeElement = playheadElement || reviewWrapper;

      if (timeElement) {
        const timeAttribute = playheadElement ? 'aria-valuenow' : 'data-playback-time';

        // Track last processed rounded time to only update on 10th-of-second changes
        let lastProcessedTime = -1;

        const timeObserver = new MutationObserver((mutations) => {
          const timeChanged = mutations.some(mutation =>
            mutation.type === 'attributes' && mutation.attributeName === timeAttribute
          );

          if (timeChanged) {
            const currentTimeValue = timeElement.getAttribute(timeAttribute);
            if (!currentTimeValue) return;

            // Parse and round to 1 decimal place (10ths of a second)
            const parsedTime = parseFloat(currentTimeValue);
            const roundedTime = Math.round(parsedTime * 10) / 10;

            // Only process if the rounded time actually changed
            if (roundedTime === lastProcessedTime) {
              return; // Skip this update, still on same 10th of second
            }

            lastProcessedTime = roundedTime;

            // Cancel any pending visibility update
            if (visibilityRafId !== null) {
              cancelAnimationFrame(visibilityRafId);
            }
            // Defer visibility update to next frame
            visibilityRafId = requestAnimationFrame(() => {
              visibilityRafId = null;
              updateOverlayTimelineVisibility(engine, SLIDER_OVERLAYS, isEngineReady);
            });
          }
        });

        // Observe only the time attribute changes
        timeObserver.observe(timeElement, {
          attributes: true,
          attributeFilter: [timeAttribute]
        });

        // Store cleanup function
        unsubStateChanged = () => {
          timeObserver.disconnect();
        };
      }
    }


  // Set initial visibility based on current timeline position
  updateOverlayTimelineVisibility(engine, SLIDER_OVERLAYS, isEngineReady);

  // Return controller object
  return {
    updateForActivePage,
    cleanup: () => {
      window.removeEventListener('cesdk-relayout', handleResize);
      try { unsubSliders?.(); } catch {}
      try { unsubPages?.(); } catch {}
      try { ro.disconnect(); } catch {}
      try { unsubZoomLevelChanged?.(); } catch {}
      if (zoomTimeout) clearTimeout(zoomTimeout);
      try { unsubStateChanged?.(); } catch {}
      // Cancel any pending visibility RAF
      if (visibilityRafId !== null) {
        cancelAnimationFrame(visibilityRafId);
        visibilityRafId = null;
      }
      overlays.forEach((el) => { if (el.parentElement === host) el.remove(); });
      if (host.parentElement) host.parentElement.removeChild(host);
    }
  };
}

/** --------------------------------------
 * Scene helpers
 * ------------------------------------- */
export function createSliderBlock(
  engine: CreativeEngine,
  parentPage: number,
  meta?: Partial<SliderMeta>
): number {
  const id = `slider-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const slider: SliderMeta = withDefaults({ id, ...meta });

  const block = engine.block.create('graphic');
  setSliderMeta(engine, block, slider);
  engine.block.setName?.(block, slider.label ? `Slider – ${slider.label}` : 'Slider');

  // Centered placement
  const pageW = engine.block.getWidth?.(parentPage) ?? 1920;
  const pageH = engine.block.getHeight?.(parentPage) ?? 1080;
  const w = Math.round(pageW * 0.58);
  const h = Math.round(slider.style?.heightPx ?? 64);
  const x = Math.round((pageW - w) / 2);
  const y = Math.round(pageH * 0.52);

  engine.block.setWidthMode?.(block, 'Absolute');
  engine.block.setHeightMode?.(block, 'Absolute');
  engine.block.setWidth?.(block, w);
  engine.block.setHeight?.(block, h);
  engine.block.setPositionX?.(block, x);
  engine.block.setPositionY?.(block, y);

  // Neutral shape to make the block visible/selectable
  try {
    const shape = engine.block.createShape?.('rect');
    engine.block.setShape?.(block, shape);

    // Disable fill/stroke to prevent inspector from reading invalid block references
    if (typeof engine.block.setFillEnabled === 'function') {
      try {
        engine.block.setFillEnabled(block, false);
      } catch {}
    }
    if (typeof engine.block.setStrokeEnabled === 'function') {
      try {
        engine.block.setStrokeEnabled(block, false);
      } catch {}
    }
  } catch {}

  engine.block.appendChild?.(parentPage, block);
  engine.block.setMetadata(block, BLOCK_TYPE, SLIDER_META_KEY);

  engine.block.setScopeEnabled(block, 'layer/rotate', false);

  return block;
}

export function setSliderMeta(engine: CreativeEngine, blockId: number, meta: SliderMeta): void {
  engine.block.setMetadata?.(blockId, SLIDER_META_KEY, JSON.stringify(withDefaults(meta)));
}

export function getSliderMeta(engine: CreativeEngine, blockId: number): SliderMeta {
  try {
    const raw = (engine.block as any).getMetadata?.(blockId, SLIDER_META_KEY);
    if (raw) return withDefaults(JSON.parse(raw));
  } catch {}
  return withDefaults({ id: `slider-${blockId}` });
}

export function safeHasSliderMeta(engine: CreativeEngine, id: number): boolean {
  try {
    const raw = (engine.block as any).getMetadata?.(id, SLIDER_META_KEY);
    if (!raw) return false;
    try { JSON.parse(raw); } catch { return false; }
    return true;
  } catch { return false; }
}

export function findAllSliders(engine: CreativeEngine): number[] {
  const pages: number[] = engine.scene?.getPages?.() ?? [];
  const out: number[] = [];
  const stack: number[] = [...pages];
  while (stack.length) {
    const id = stack.pop()!;
    // Validate block ID before accessing it
    if (!Number.isInteger(id) || id <= 0 || id === 4294967295) {
      continue;
    }
    try {
      if (!engine.block.isValid?.(id)) {
        continue;
      }
      const children: number[] = engine.block.getChildren?.(id) ?? [];
      for (const c of children) stack.push(c);
      if (safeHasSliderMeta(engine, id)) out.push(id);
    } catch {
      // Ignore errors from invalid blocks
    }
  }
  return out;
}

/** --------------------------------------
 * Overlay DOM helpers
 * ------------------------------------- */

//These are the default properties to change for the slider
export function defaultStyle(): Required<NonNullable<SliderMeta['style']>> {
  return {
    heightPx: 64,
    trackHeightPx: 20,
    trackRadiusPx: 999,
    outlineColor: 'rgba(255,255,255,0.95)',
    outlineSizePx: 6,
    trackColor: 'rgba(255,255,255,0.15)',
    fillColor: 'rgba(100, 150, 204, 1)',
    thumbColor: '#FFFFFF',
    thumbRadiusPx: 32,
    tickColor: 'rgba(255,255,255,0.7)',
    labelColor: '#FFFFFF',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSizePx: 18,
    labelUppercase: true,
    showActiveLabelOverlay: false
  };
}

export function withDefaults(meta: Partial<SliderMeta>): SliderMeta {
  const m: SliderMeta = {
    id: meta.id || `slider-${Math.random().toString(36).slice(2, 8)}`,
    elementType: SLIDER_META_KEY,
    min: meta.min ?? 0,
    max: meta.max ?? 100,
    step: meta.step ?? 1,
    value: meta.value ?? 50,
    orientation: meta.orientation ?? 'horizontal',
    label: meta.label,
    discrete: meta.discrete ?? false,
    steps: meta.steps ?? [
      { value: 0, text: 'STEP 1' },
      { value: 50, text: 'STEP 2' },
      { value: 100, text: 'STEP 3' }
    ],
    style: { ...defaultStyle(), ...(meta.style ?? {}) }
  };
  // if (m.style.labelUppercase && m.steps) m.steps = m.steps.map((s) => ({ ...s, text: s.text.toUpperCase() }));
  m.value = Math.min(m.max, Math.max(m.min, m.value));
  return m;
}

export function createSliderOverlay(
  engine: CreativeEngine,
  sliderId: number,
  root: HTMLElement,
  interactive: boolean
): HTMLDivElement {
  // Wrapper mirrors the block's screen-space rect 1:1
  const wrap = document.createElement('div');
  wrap.style.position = 'absolute';
  wrap.style.pointerEvents = 'none';
  wrap.style.willChange = 'left,top,width,height';

  const meta = getSliderMeta(engine, sliderId);
  const style = { ...defaultStyle(), ...(meta.style ?? {}) };

  wrap.dataset.idecideOverlay = 'slider';
  wrap.dataset.blockId = String(sliderId);
  wrap.dataset.baseHeight = String(style.heightPx);
  wrap.dataset.baseTrackHeight = String(style.trackHeightPx);
  wrap.dataset.baseOutline = String(style.outlineSizePx);
  wrap.dataset.baseFontSize = String(style.fontSizePx ?? 18);
  wrap.dataset.baseThumbSize = String((style.thumbRadiusPx ?? 32) * 2);

  const ownerPage = findOwningPage(engine, sliderId);
  wrap.dataset.ownerPage = ownerPage != null ? String(ownerPage) : '';

  root.appendChild(wrap);

  // Full-size container
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.inset = '0';
  container.style.display = 'flex';
  container.style.alignItems = 'center';
  container.style.justifyContent = 'center';
  container.style.pointerEvents = 'none';

  const inner = document.createElement('div');
  inner.style.position = 'relative';
  inner.style.width = '100%';
  inner.style.height = '100%';
  inner.style.pointerEvents = 'none';

  // Outline pill
  const outline = document.createElement('div');
  outline.dataset.role = 'outline';
  outline.style.position = 'absolute';
  outline.style.left = `${style.outlineSizePx}px`;
  outline.style.right = `${style.outlineSizePx}px`;
  outline.style.top = `calc(50% - ${Math.floor(style.trackHeightPx / 2) + style.outlineSizePx}px)`;
  outline.style.height = `${style.trackHeightPx + style.outlineSizePx * 2}px`;
  outline.style.border = `${style.outlineSizePx}px solid ${style.outlineColor}`;
  outline.style.borderRadius = `${style.trackRadiusPx}px`;
  outline.style.pointerEvents = 'none';
  outline.style.boxSizing = 'border-box';

  // Track background
  const track = document.createElement('div');
  track.dataset.role = 'track';
  track.style.position = 'absolute';
  track.style.left = `${style.outlineSizePx * 2}px`;
  track.style.right = `${style.outlineSizePx * 2}px`;
  track.style.top = `calc(50% - ${Math.floor(style.trackHeightPx / 2)}px)`;
  track.style.height = `${style.trackHeightPx}px`;
  track.style.background = style.trackColor;
  track.style.borderRadius = `${style.trackRadiusPx}px`;
  track.style.pointerEvents = 'none';

  // Progress fill
  const fill = document.createElement('div');
  fill.dataset.role = 'fill';
  fill.style.position = 'absolute';
  fill.style.left = `${style.outlineSizePx * 2}px`;
  fill.style.top = track.style.top;
  fill.style.height = track.style.height;
  fill.style.background = style.fillColor;
  fill.style.borderRadius = `${style.trackRadiusPx}px`;
  fill.style.width = '0%';
  fill.style.pointerEvents = 'none';

  // Step labels
  const labels = document.createElement('div');
  labels.dataset.role = 'labels';
  labels.style.position = 'absolute';
  labels.style.left = `${style.outlineSizePx * 2}px`;
  labels.style.right = `${style.outlineSizePx * 2}px`;
  labels.style.top = `calc(50% + ${Math.ceil(style.trackHeightPx / 2) + style.outlineSizePx + 6}px)`;
  labels.style.display = 'flex';
  labels.style.justifyContent = 'space-between';
  labels.style.fontFamily = style.fontFamily;
  labels.style.fontSize = `${style.fontSizePx}px`;
  labels.style.color = style.labelColor;
  labels.style.pointerEvents = 'none';

  const activeLabel = document.createElement('div');
  activeLabel.dataset.role = 'activeLabel';
  activeLabel.style.position = 'absolute';
  activeLabel.style.left = '0';
  activeLabel.style.right = '0';
  activeLabel.style.top = `calc(50% + ${Math.ceil(style.trackHeightPx / 2) + style.outlineSizePx + 28}px)`;
  activeLabel.style.textAlign = 'center';
  activeLabel.style.fontFamily = style.fontFamily;
  activeLabel.style.fontSize = `${Math.round(style.fontSizePx * 1.6)}px`;
  activeLabel.style.color = style.labelColor;
  activeLabel.style.fontWeight = '600';
  activeLabel.style.pointerEvents = 'none';
  activeLabel.style.display = style.showActiveLabelOverlay ? 'block' : 'none';

  const labelSpans: HTMLSpanElement[] = [];
  (meta.steps ?? []).forEach((st) => {
    const span = document.createElement('span');
    span.textContent = style.labelUppercase ? st.text.toUpperCase() : st.text;
    span.style.pointerEvents = interactive ? 'auto' : 'none';
    span.style.cursor = interactive ? 'pointer' : 'default';
    span.style.userSelect = 'none';
    if (interactive) span.addEventListener('click', () => setValue(st.value));
    labels.appendChild(span);
    labelSpans.push(span);
  });


  // Interactive input across the track
  const input = document.createElement('input');
  input.dataset.role = 'input';
  input.type = 'range';
  input.style.position = 'absolute';
  input.style.left = `${style.outlineSizePx * 2}px`;
  input.style.right = `${style.outlineSizePx * 2}px`;

  input.style.top = `calc(50% - ${Math.floor(style.trackHeightPx / 2)}px)`;
  input.style.height = `${style.trackHeightPx}px`;
  input.style.background = 'transparent';
  (input.style as any).webkitAppearance = 'none';
  (input.style as any).appearance = 'none';
  input.style.pointerEvents = interactive ? 'auto' : 'none';

  input.style.setProperty('--thumb-color', style.thumbColor ?? '#FFFFFF');
  input.style.setProperty('--thumb-nudge', '-1px');

  input.classList.add('idecide-range');

  const attachMetaToInput = (m: SliderMeta) => {
    if (m.steps && m.steps.length >= 2) {
      input.min = String(m.steps[0].value);
      input.max = String(m.steps[m.steps.length - 1].value);
      input.step = String(m.step);
    } else {
      input.min = String(m.min);
      input.max = String(m.max);
      input.step = String(m.step);
    }
    input.value = String(m.value);
  };

  const setValue = (v: number) => {
    const current = getSliderMeta(engine, sliderId);
    const next = withDefaults({ ...current, value: v });
    setSliderMeta(engine, sliderId, next);
    attachMetaToInput(next);
    updateProgress(next);
    mirrorVariables(engine, next);
  };

  const updateProgress = (m: SliderMeta) => {
    const min = Number(input.min);
    const max = Number(input.max);
    const val = Number(input.value);
    const t = (val - min) / Math.max(1e-9, max - min);
    const trackWidthPx = outline.clientWidth - style.outlineSizePx * 2;
    const fillWidth = Math.max(0, Math.min(trackWidthPx, t * trackWidthPx));
    fill.style.width = `${fillWidth}px`;

    let activeText = String(val);
    if (m.steps && m.steps.length) {
      const idx = nearestStepIndex(m, val);
      labelSpans.forEach((sp, i) => {
        sp.style.fontWeight = i === idx ? '700' : '400';
        sp.style.opacity = i === idx ? '1' : '0.85';
      });
      activeText = m.steps[idx].text;
    }
    activeLabel.textContent = activeText;
  };

  input.addEventListener('input', () => {
    // const _current = getSliderMeta(engine, sliderId);
    let v = Number(input.value);
    // If you want to hard-snap in discrete mode, uncomment:
    // if (current.discrete && current.steps && current.steps.length >= 2) {
    //   const idx = nearestStepIndex(current, v);
    //   v = current.steps[idx].value;
    // }
    setValue(v);

    // In the editor, keep the block selected and the panel open
    if (engine) {
      engine.block.setSelected(sliderId, true);
    //   try { cesdk.ui.openPanel(SLIDER_PANEL_ID); } catch {}
    }
  });

  // Initial state
  attachMetaToInput(meta);
  updateProgress(meta);
  mirrorVariables(engine, meta);

  // Compose DOM
  inner.appendChild(outline);
  inner.appendChild(track);
  inner.appendChild(fill);
  inner.appendChild(labels);
  inner.appendChild(activeLabel);
  inner.appendChild(input);
  container.appendChild(inner);
  wrap.appendChild(container);

  // First layout uses the shared helper to avoid the initial left-shift
  requestAnimationFrame(() => positionOverlayToBlock(engine, sliderId, wrap, updateSliderOverlayGeometry));

  return wrap;
}

/** Mirror current slider state into CE variables so templates can bind to them. */
export function mirrorVariables(engine: CreativeEngine, m: SliderMeta): void {
  try { engine.variable?.setString?.(`slider:${m.id}:value`, String(m.value)); } catch {}
  try {
    const text = m.steps?.length ? m.steps[nearestStepIndex(m, m.value)].text : String(m.value);
    engine.variable?.setString?.(`slider:${m.id}:text`, text);
  } catch {}
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(DYNAMIC_VARIABLES_UPDATED_EVENT, {
      detail: {
        id: m.id,
        value: m.value
      }
    }));
  }
}

/** Scale overlay internals when the block height changes. */
export function updateSliderOverlayGeometry(el: HTMLElement): void {
  if (!el || el.dataset.idecideOverlay !== 'slider') return;

  const baseH       = Number(el.dataset.baseHeight)      || 64;
  const baseTrack   = Number(el.dataset.baseTrackHeight) || 36;
  const baseOutline = Number(el.dataset.baseOutline)     || 6;
  const baseFont    = Number(el.dataset.baseFontSize)    || 18;
  const baseThumb   = Number(el.dataset.baseThumbSize)   || 32;

  const h = el.clientHeight || baseH;
  const scale = h / baseH;

  const trackH   = Math.max(6,  Math.round(baseTrack   * scale));
  const outlineW = Math.max(2,  Math.round(baseOutline * scale));
  const fontPx   = Math.max(10, Math.round(baseFont    * scale));
  const thumbSz  = Math.max(10, Math.round(baseThumb   * scale));
  const ringPx   = Math.max(1,  Math.round(2           * scale));

  const q = (role: string) => el.querySelector(`[data-role="${role}"]`) as HTMLDivElement | HTMLInputElement | null;

  const outline = q('outline');
  const track   = q('track');
  const fill    = q('fill');
  const labels  = q('labels');
  const input   = q('input') as HTMLInputElement | null;
  const active  = q('activeLabel');

  // const trackTop   = Math.round(h / 2 - trackH / 2);
  // const outlineTop = Math.round(trackTop - outlineW);

  // visual thumb height includes the ring/border
  const thumbVisual = thumbSz + ringPx * 2;

  // how much the thumb sticks out beyond the track on the bottom (and top)
  const overhang = Math.max(0, (thumbVisual - trackH) / 2);

  const LABEL_GAP = 6;         // gap between track and labels
  const ACTIVE_EXTRA = 22;     // extra below labels for activeLabel

  // current vertical center
  const mid = h / 2;

  // existing positions (keep your track/outline math)
  const trackTop   = mid - trackH / 2;
  const outlineTop = trackTop - outlineW;

  if (outline) {
    outline.style.left = `${outlineW}px`;
    outline.style.right = `${outlineW}px`;
    outline.style.top = `${outlineTop}px`;
    outline.style.height = `${trackH + outlineW * 2}px`;
    outline.style.borderWidth = `${outlineW}px`;
  }
  if (track) {
    track.style.left = `${outlineW * 2}px`;
    track.style.right = `${outlineW * 2}px`;
    track.style.top = `${trackTop}px`;
    track.style.height = `${trackH}px`;
  }
  if (fill) {
    fill.style.left = `${outlineW * 2}px`;
    fill.style.top = `${trackTop}px`;
    fill.style.height = `${trackH}px`;
  }
  if (input) {
    input.style.left = `${outlineW * 2}px`;
    input.style.right = `${outlineW * 2}px`;
    input.style.top = `${trackTop}px`;

    input.style.height = `${trackH}px`;

    input.style.setProperty('--track-h', `${trackH}px`);
    input.style.setProperty('--thumb-size', `${thumbSz}px`);
    input.style.setProperty('--thumb-ring', `${ringPx}px`);
  }
  if (labels) {
    labels.style.left = `${outlineW * 2}px`;
    labels.style.right = `${outlineW * 2}px`;
    labels.style.top = `${Math.round(mid + trackH / 2 + outlineW + LABEL_GAP + overhang)}px`;
    labels.style.fontSize = `${fontPx}px`;
  }
  if (active) {
    active.style.top = `${Math.round(mid + trackH / 2 + outlineW + LABEL_GAP + ACTIVE_EXTRA + overhang)}px`;
    active.style.fontSize = `${Math.round(fontPx * 1.6)}px`;
  }
}

/** Replace an existing overlay for a slider without losing layout. */
export function refreshSliderOverlay(
  engine: CreativeEngine,
  sliderId: number
): void {
  const oldEl = SLIDER_OVERLAYS.get(sliderId);
  if (!oldEl) return;
  const root = oldEl.parentElement as HTMLElement | null;
  oldEl.remove();
  if (!root) {
    SLIDER_OVERLAYS.delete(sliderId);
    return;
  }
  const next = createSliderOverlay(engine, sliderId, root, true);
  SLIDER_OVERLAYS.set(sliderId, next);
  requestAnimationFrame(() => positionOverlayToBlock(engine, sliderId, next, updateSliderOverlayGeometry));
}

/** --------------------------------------
 * Utilities
 * ------------------------------------- */
export function nearestStepIndex(m: SliderMeta, v: number): number {
  if (!m.steps || m.steps.length === 0) return 0;
  let best = 0, bestD = Infinity;
  m.steps.forEach((s, i) => { const d = Math.abs(s.value - v); if (d < bestD) { bestD = d; best = i; } });
  return best;
}
